import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { useStoreSelector } from "../../../../../src/lib/store";
import type { PatternTrack } from "../../../../../src/lib/audio";
import {
  hasArrangePatternClipboard,
  hasArrangeTrackClipboard,
} from "../../lib/arrangeClipboard";
import {
  patternHint,
  patternLabel,
  trackHasContent,
} from "../../lib/patternHelpers";
import { trackColorHex, trackTintBackground } from "../../lib/trackColors";
import {
  ARRANGE_HEADER_PX,
  ARRANGE_ROW_PX,
  restoreArrangePlayingHighlight,
  setArrangePlayheadHost,
} from "../../lib/arrangePlayhead";
import {
  registerArrangeMeterBars,
  setArrangeMeterPump,
} from "../../lib/arrangeMeters";
import { getLabActions } from "../../labActions";
import { createEmptyTelemetry } from "../../../../../src/lib/audio";
import { projectStore } from "../../store/projectStore";
import { transportStore } from "../../store/transportStore";
import { uiStore } from "../../store/uiStore";
import {
  ArrangeContextMenu,
  type ArrangeContextTarget,
  type ArrangeMenuAction,
} from "./ArrangeContextMenu";
import { ArrangeLoopBrace, ARRANGE_LOOP_COL_PX } from "./ArrangeLoopBrace";
import {
  dockActionsBtn,
  dockBodyColumn,
  dockFieldControl,
} from "./dockChrome";

type ArrangeSelection =
  | { kind: "pattern"; patternId: string }
  | { kind: "track"; patternId: string; track: number };

type RenameEdit = { patternId: string };

const LOOP_COL_PX = ARRANGE_LOOP_COL_PX;
const PATTERN_COL_PX = 138;
const CELL_PX = 33;
const ROW_PX = ARRANGE_ROW_PX;
const HEADER_PX = ARRANGE_HEADER_PX;

/**
 * Renoise-style arrangement matrix: sticky pattern column + shared track grid
 * (one scrollport — columns align across every row).
 */
export function ArrangeBody({
  onBusyChange,
}: {
  onBusyChange?: (busy: boolean) => void;
}) {
  const clip = useStoreSelector(projectStore, s => s.clip);
  const trackColors = useStoreSelector(projectStore, s => s.trackColors);
  const activePatternId = useStoreSelector(uiStore, s => s.activePatternId);
  const patternLoop = useStoreSelector(uiStore, s => s.patternLoop);
  const armed = useStoreSelector(transportStore, s => s.armed);

  const [selection, setSelection] = useState<ArrangeSelection>({
    kind: "pattern",
    patternId: activePatternId,
  });
  const [renameEdit, setRenameEdit] = useState<RenameEdit | undefined>();
  const [context, setContext] = useState<
    | { target: ArrangeContextTarget; point: { x: number; y: number } }
    | undefined
  >();
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [trackDragFrom, setTrackDragFrom] = useState<number | null>(null);
  const [trackDropIndex, setTrackDropIndex] = useState<number | null>(null);
  const [clipboardTick, setClipboardTick] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const renameRef = useRef<HTMLInputElement>(null);
  const matrixRef = useRef<HTMLDivElement>(null);
  const playheadLineRef = useRef<HTMLDivElement>(null);
  const cursorLineRef = useRef<HTMLDivElement>(null);

  const patterns = clip.patterns;
  const trackCount = Math.max(
    1,
    ...patterns.map(pattern => pattern.tracks.length),
  );
  const busy = Boolean(context || renameEdit);

  // Loop brace lives in a sticky rail beside the matrix — not a grid column.
  const gridTemplateColumns =
    `${PATTERN_COL_PX}px repeat(${trackCount}, ${CELL_PX}px)`;

  useLayoutEffect(() => {
    const playheadLine = playheadLineRef.current;
    const cursorLine = cursorLineRef.current;
    const matrix = matrixRef.current;
    if (!playheadLine || !cursorLine || !matrix) {
      return;
    }
    setArrangePlayheadHost({
      playheadLine,
      cursorLine,
      matrix,
      getPatterns: () => projectStore.getValue().clip.patterns,
    });
    setArrangeMeterPump({
      getTelemetry: () => getLabActions()?.getTelemetry() ?? createEmptyTelemetry(),
      isPlaying: () => transportStore.getValue().playing,
    });
    return () => {
      setArrangePlayheadHost(null);
      setArrangeMeterPump(null);
    };
  }, []);

  // React re-renders wipe imperative gold-row styles — restore from last playhead.
  useLayoutEffect(() => {
    restoreArrangePlayingHighlight();
  });

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  useEffect(() => {
    setSelection(prev => {
      const activeStillThere = patterns.some(pattern => pattern.id === activePatternId);
      const prevStillThere = patterns.some(pattern => pattern.id === prev.patternId);
      if (!prevStillThere) {
        return { kind: "pattern", patternId: activeStillThere ? activePatternId : (patterns[0]?.id ?? activePatternId) };
      }
      // Follow active when it moves from tracker / boot — local selection otherwise
      // stays on the default P0 from first paint and double-tints with the real active row.
      if (prev.patternId === activePatternId || !activeStillThere) {
        return prev;
      }
      if (prev.kind === "track") {
        return { kind: "track", patternId: activePatternId, track: prev.track };
      }
      return { kind: "pattern", patternId: activePatternId };
    });
  }, [patterns, activePatternId]);

  useEffect(() => {
    if (!renameEdit) {
      return;
    }
    renameRef.current?.focus();
    renameRef.current?.select();
  }, [renameEdit]);

  const bumpClipboard = useCallback(() => {
    setClipboardTick(tick => tick + 1);
  }, []);

  function selectPatternRow(patternId: string): void {
    setSelection({ kind: "pattern", patternId });
    getLabActions()?.selectPattern(patternId, { focus: false });
    queueMicrotask(() => panelRef.current?.focus());
  }

  function openTrack(patternId: string, track: number): void {
    setSelection({ kind: "track", patternId, track });
    // Keep arrange focus so ⌘/Ctrl+C/V hit the panel handler (not the tracker).
    getLabActions()?.openPatternTrack(patternId, track, { focus: false });
    queueMicrotask(() => {
      panelRef.current?.focus();
      matrixRef.current
        ?.querySelector<HTMLElement>(
          `[data-arrange-cell="${patternId}:${track}"]`,
        )
        ?.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
  }

  /** Move selection one cell in the pattern×track matrix (arrow keys). */
  function moveArrangeSelection(dx: number, dy: number): boolean {
    if (dx === 0 && dy === 0) {
      return false;
    }
    const patternIds = patterns.map(pattern => pattern.id);
    if (patternIds.length === 0 || trackCount <= 0) {
      return false;
    }

    if (selection.kind === "pattern") {
      const patternIndex = patternIds.indexOf(selection.patternId);
      if (patternIndex < 0) {
        return false;
      }
      if (dx > 0) {
        openTrack(selection.patternId, 0);
        return true;
      }
      if (dy === 0) {
        return false;
      }
      const nextIndex = Math.max(0, Math.min(patternIds.length - 1, patternIndex + dy));
      if (nextIndex === patternIndex) {
        return false;
      }
      selectPatternRow(patternIds[nextIndex]!);
      return true;
    }

    const patternIndex = patternIds.indexOf(selection.patternId);
    if (patternIndex < 0) {
      return false;
    }
    // Left from track 0 → pattern column.
    if (dx < 0 && selection.track === 0) {
      selectPatternRow(selection.patternId);
      return true;
    }
    const nextTrack = Math.max(0, Math.min(trackCount - 1, selection.track + dx));
    const nextPatternIndex = Math.max(
      0,
      Math.min(patternIds.length - 1, patternIndex + dy),
    );
    if (nextTrack === selection.track && nextPatternIndex === patternIndex) {
      return false;
    }
    openTrack(patternIds[nextPatternIndex]!, nextTrack);
    return true;
  }

  function commitRename(patternId: string, raw: string): void {
    setRenameEdit(undefined);
    getLabActions()?.renamePattern(patternId, raw.trim());
  }

  function runMenuAction(action: ArrangeMenuAction): void {
    const actions = getLabActions();
    if (!actions || !context) {
      return;
    }
    const target = context.target;
    if (target.kind === "loopBrace") {
      if (action === "bounceToWav") {
        void actions.bounce("range");
      } else if (action === "bounceToSampler") {
        void actions.bounceToSampler();
      }
      return;
    }
    if (target.kind === "pattern") {
      switch (action) {
        case "rename":
          setRenameEdit({ patternId: target.id });
          break;
        case "duplicate":
          actions.duplicatePattern(target.id);
          break;
        case "delete":
          actions.deletePattern(target.id);
          break;
        case "copy":
          actions.copyPattern(target.id);
          bumpClipboard();
          break;
        case "paste":
          actions.pastePattern(target.id);
          bumpClipboard();
          break;
        default:
          break;
      }
      return;
    }
    if (action === "copyTrack") {
      actions.copyTrack(target.patternId, target.track);
      bumpClipboard();
    } else if (action === "pasteTrack") {
      actions.pasteTrack(target.patternId, target.track);
      bumpClipboard();
    }
  }

  function handleArrangeShortcut(event: {
    metaKey: boolean;
    ctrlKey: boolean;
    altKey?: boolean;
    key: string;
    preventDefault: () => void;
    stopPropagation: () => void;
  }): boolean {
    if (renameEdit || context) {
      return false;
    }
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();
    const actions = getLabActions();
    if (!actions) {
      return false;
    }

    // M — toggle mute on the selected track cell (per-pattern).
    if (!mod && !event.altKey && key === "m" && selection.kind === "track") {
      event.preventDefault();
      event.stopPropagation();
      actions.togglePatternMute(selection.patternId, selection.track);
      queueMicrotask(() => panelRef.current?.focus());
      return true;
    }

    // A — loop brace over all patterns; L — loop brace on the current pattern only.
    if (!mod && !event.altKey && (key === "a" || key === "l")) {
      const count = patterns.length;
      if (count <= 0) {
        return false;
      }
      event.preventDefault();
      event.stopPropagation();
      if (key === "a") {
        actions.setPatternLoop({ start: 0, length: count });
      } else {
        const index = patterns.findIndex(p => p.id === selection.patternId);
        actions.setPatternLoop({ start: index < 0 ? 0 : index, length: 1 });
      }
      queueMicrotask(() => panelRef.current?.focus());
      return true;
    }

    // Arrow keys — walk the pattern × track matrix.
    if (!mod && !event.altKey) {
      let dx = 0;
      let dy = 0;
      if (key === "arrowleft") {
        dx = -1;
      } else if (key === "arrowright") {
        dx = 1;
      } else if (key === "arrowup") {
        dy = -1;
      } else if (key === "arrowdown") {
        dy = 1;
      }
      if ((dx !== 0 || dy !== 0) && moveArrangeSelection(dx, dy)) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
    }

    if (!mod) {
      return false;
    }
    if (key !== "c" && key !== "x" && key !== "v" && key !== "d") {
      return false;
    }
    event.preventDefault();
    event.stopPropagation();
    if (key === "d") {
      // Duplicate the pattern under the selection (track cell → its pattern).
      actions.duplicatePattern(selection.patternId);
      const nextId = uiStore.getValue().activePatternId;
      setSelection(
        selection.kind === "track"
          ? { kind: "track", patternId: nextId, track: selection.track }
          : { kind: "pattern", patternId: nextId },
      );
      queueMicrotask(() => panelRef.current?.focus());
      return true;
    }
    if (selection.kind === "track") {
      if (key === "c") {
        actions.copyTrack(selection.patternId, selection.track);
        bumpClipboard();
      } else if (key === "v") {
        actions.pasteTrack(selection.patternId, selection.track);
        bumpClipboard();
      }
      // ⌘X on a track: no cut-track action yet (context menu is copy/paste only).
      return true;
    }
    if (key === "c") {
      actions.copyPattern(selection.patternId);
      bumpClipboard();
    } else if (key === "x") {
      actions.cutPattern(selection.patternId);
      bumpClipboard();
    } else if (key === "v") {
      actions.pastePattern(selection.patternId);
      bumpClipboard();
    }
    return true;
  }

  function onPanelKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    handleArrangeShortcut(event);
  }

  // Capture phase so we beat the tracker's host keydown when focus is in Arrange.
  useEffect(() => {
    function onWindowKeyDown(event: KeyboardEvent): void {
      const dock = document.getElementById("patterns-dock");
      const active = document.activeElement;
      if (!dock || !(active instanceof Node) || !dock.contains(active)) {
        return;
      }
      if (
        active instanceof HTMLInputElement
        || active instanceof HTMLTextAreaElement
        || active instanceof HTMLSelectElement
        || (active instanceof HTMLElement && active.isContentEditable)
      ) {
        return;
      }
      handleArrangeShortcut(event);
    }
    window.addEventListener("keydown", onWindowKeyDown, true);
    return () => window.removeEventListener("keydown", onWindowKeyDown, true);
  }, [selection, renameEdit, context, patterns, trackCount]);

  function onDragStart(index: number, event: ReactDragEvent): void {
    setDragFrom(index);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  }

  function onDragOverRow(index: number, event: ReactDragEvent): void {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropIndex(index);
  }

  function onDropRow(index: number, event: ReactDragEvent): void {
    event.preventDefault();
    const from = dragFrom ?? Number(event.dataTransfer.getData("text/plain"));
    setDragFrom(null);
    setDropIndex(null);
    if (!Number.isFinite(from) || from === index) {
      return;
    }
    getLabActions()?.reorderPatterns(from, index);
  }

  function onDragEnd(): void {
    setDragFrom(null);
    setDropIndex(null);
  }

  function onTrackDragStart(track: number, event: ReactDragEvent): void {
    setTrackDragFrom(track);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-adam-track", String(track));
    event.dataTransfer.setData("text/plain", `track:${track}`);
  }

  function onTrackDragOver(track: number, event: ReactDragEvent): void {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setTrackDropIndex(track);
  }

  function onTrackDrop(track: number, event: ReactDragEvent): void {
    event.preventDefault();
    const raw =
      event.dataTransfer.getData("application/x-adam-track")
      || event.dataTransfer.getData("text/plain").replace(/^track:/, "");
    const from = trackDragFrom ?? Number(raw);
    setTrackDragFrom(null);
    setTrackDropIndex(null);
    if (!Number.isFinite(from) || from === track) {
      return;
    }
    getLabActions()?.reorderTracks(from, track);
  }

  function onTrackDragEnd(): void {
    setTrackDragFrom(null);
    setTrackDropIndex(null);
  }

  void clipboardTick;
  const canPastePattern = hasArrangePatternClipboard();
  const canPasteTrack = hasArrangeTrackClipboard();

  return (
    <div
      ref={panelRef}
      className={`${dockBodyColumn} outline-none`}
      tabIndex={0}
      onKeyDown={onPanelKeyDown}
      onMouseDown={event => {
        const target = event.target;
        if (
          target instanceof HTMLInputElement
          || target instanceof HTMLTextAreaElement
          || target instanceof HTMLSelectElement
          || (target instanceof HTMLElement && target.isContentEditable)
        ) {
          return;
        }
        // Keep keyboard focus on the arrange host so the dock outline + M/⌘C work.
        panelRef.current?.focus({ preventScroll: true });
      }}
    >
      {/* Single scrollport: sticky loop rail + pattern col, track header sticky-top */}
      <div className="arrange-scroll relative min-h-0 flex-1 overflow-auto bg-lab-panel-deep">
        <div className="inline-flex min-w-full items-stretch">
          {/* Sticky left rail — brace + loop gutters stay put on horizontal scroll */}
          <div
            className="sticky left-0 z-[25] shrink-0 border-r border-lab-line"
            style={{ width: LOOP_COL_PX }}
            title="Loop brace"
          >
            <div
              className="sticky top-0 z-30 border-b border-lab-line"
              style={{ height: HEADER_PX, backgroundColor: "var(--arrange-header)" }}
              role="columnheader"
              aria-label="Loop brace"
            />
            {patterns.map((pattern, index) => {
              const isActive = pattern.id === activePatternId;
              const isSelectedPattern =
                selection.patternId === pattern.id && selection.kind === "pattern";
              const rowBgSolid = isActive || isSelectedPattern
                ? "var(--arrange-row-active)"
                : index % 2 === 0
                ? "var(--arrange-row-a)"
                : "var(--arrange-row-b)";
              const inLoop =
                index >= patternLoop.start && index < patternLoop.start + patternLoop.length;
              const gutterBg = inLoop ? "#2a2014" : rowBgSolid;
              return (
                <div
                  key={`loop-${pattern.id}`}
                  className="border-b border-lab-line"
                  data-arrange-pattern={pattern.id}
                  data-arrange-bg={gutterBg}
                  style={{ height: ROW_PX, backgroundColor: gutterBg }}
                  aria-hidden={true}
                />
              );
            })}
            <ArrangeLoopBrace
              patternCount={patterns.length}
              range={patternLoop}
              onChange={next => getLabActions()?.setPatternLoop(next)}
              onContextMenu={event => {
                setContext({
                  target: { kind: "loopBrace" },
                  point: { x: event.clientX, y: event.clientY },
                });
              }}
            />
          </div>

          <div
            ref={matrixRef}
            className="relative inline-grid min-w-0 grow"
            style={{
              gridTemplateColumns,
              // Explicit header track — must match ARRANGE_HEADER_PX (playhead math).
              gridTemplateRows: `${HEADER_PX}px`,
              gridAutoRows: `${ROW_PX}px`,
            }}
            role="grid"
            aria-label="Arrangement matrix"
          >
            {/* Imperative edit cursor — always on, gray; playhead on top when both show */}
            <div
              ref={cursorLineRef}
              className="pointer-events-none absolute top-0 right-0 left-0 z-[35] opacity-0 will-change-transform"
              style={{ height: 1, backgroundColor: "#8b95a5", transform: "translate3d(0, 0, 0)" }}
              aria-hidden={true}
            />
            {/* Imperative playhead — top→bottom through the playing pattern row */}
            <div
              ref={playheadLineRef}
              className="pointer-events-none absolute top-0 right-0 left-0 z-40 bg-[var(--cursor)] opacity-0 will-change-transform"
              style={{ height: 1, transform: "translate3d(0, 0, 0)" }}
              aria-hidden={true}
            />

            <div
              className="sticky top-0 z-30 flex items-end border-b border-r border-lab-line px-1.5 pb-1 text-[9px] tracking-[0.06em] text-lab-muted uppercase"
              style={{
                height: HEADER_PX,
                left: LOOP_COL_PX,
                backgroundColor: "var(--arrange-header)",
              }}
              role="columnheader"
            >
              Pat
            </div>
            {Array.from({ length: trackCount }, (_, track) => {
              const tint = trackColorHex(trackColors[track]);
              const dropTarget =
                trackDropIndex === track
                && trackDragFrom !== null
                && trackDragFrom !== track;
              return (
                <div
                  key={`h-${track}`}
                  className={[
                    "sticky top-0 z-20 flex items-center justify-center border-b border-lab-line font-mono text-[9px]",
                    tint ? "text-slate-100" : "text-lab-muted",
                    dropTarget ? "outline outline-1 outline-lab-accent -outline-offset-1" : "",
                    trackDragFrom === track ? "opacity-50" : "",
                  ].join(" ")}
                  style={{
                    height: HEADER_PX,
                    // Dark wash (same idea as tracker heads) so digits stay readable.
                    backgroundColor: tint
                      ? trackTintBackground("var(--arrange-tint-base)", trackColors[track], 34)
                      : "var(--arrange-header)",
                    boxShadow: tint ? `inset 0 -2px 0 ${tint}` : undefined,
                  }}
                  role="columnheader"
                  title={`Track ${track + 1} — drag to reorder`}
                  draggable
                  onDragStart={event => onTrackDragStart(track, event)}
                  onDragOver={event => onTrackDragOver(track, event)}
                  onDrop={event => onTrackDrop(track, event)}
                  onDragEnd={onTrackDragEnd}
                >
                  {String(track + 1).padStart(2, "0")}
                </div>
              );
            })}

            {patterns.map((pattern, index) => {
              const isActive = pattern.id === activePatternId;
              const isSelectedPattern =
                selection.patternId === pattern.id && selection.kind === "pattern";
              const renaming = renameEdit?.patternId === pattern.id;
              const dropTarget = dropIndex === index && dragFrom !== null && dragFrom !== index;
              // Playing gold row is applied imperatively (see arrangePlayhead) so
              // multi-pattern playback does not re-render this matrix every pattern.
              const rowBgSolid = isActive || isSelectedPattern
                ? "var(--arrange-row-active)"
                : index % 2 === 0
                ? "var(--arrange-row-a)"
                : "var(--arrange-row-b)";

              return (
                <PatternMatrixRow
                  key={pattern.id}
                  patternId={pattern.id}
                  trackCount={trackCount}
                  rowBgSolid={rowBgSolid}
                  isActive={isActive}
                  isSelectedPattern={isSelectedPattern}
                  dropTarget={dropTarget}
                  renaming={renaming}
                  label={patternLabel(pattern)}
                  hint={patternHint(pattern)}
                  nameDefault={(pattern.name ?? "").trim()}
                  selection={selection}
                  tracks={pattern.tracks}
                  renameRef={renameRef}
                  onSelectPattern={() => selectPatternRow(pattern.id)}
                  onRenameStart={() => setRenameEdit({ patternId: pattern.id })}
                  onRenameCommit={raw => commitRename(pattern.id, raw)}
                  onRenameCancel={() => setRenameEdit(undefined)}
                  onPatternContextMenu={event => {
                    event.preventDefault();
                    setContext({
                      target: { kind: "pattern", id: pattern.id },
                      point: { x: event.clientX, y: event.clientY },
                    });
                  }}
                  onOpenTrack={track => openTrack(pattern.id, track)}
                  onTrackContextMenu={(track, event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setContext({
                      target: { kind: "track", patternId: pattern.id, track },
                      point: { x: event.clientX, y: event.clientY },
                    });
                  }}
                  onDragStart={event => onDragStart(index, event)}
                  onDragOver={event => onDragOverRow(index, event)}
                  onDrop={event => onDropRow(index, event)}
                  onDragEnd={onDragEnd}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="patterns-actions flex shrink-0 flex-wrap gap-[0.35rem] border-t border-white/[0.06] px-[0.55rem] py-1.5">
        <button
          type="button"
          className={dockActionsBtn}
          onClick={() => getLabActions()?.createPattern()}
        >
          Add Pattern
        </button>
      </div>

      {context && (
        <ArrangeContextMenu
          target={context.target}
          point={context.point}
          canDelete={patterns.length > 1}
          canPastePattern={canPastePattern}
          canPasteTrack={canPasteTrack}
          canBounce={armed}
          onAction={runMenuAction}
          onClose={() => setContext(undefined)}
        />
      )}
    </div>
  );
}

type PatternMatrixRowProps = {
  patternId: string;
  trackCount: number;
  rowBgSolid: string;
  isActive: boolean;
  isSelectedPattern: boolean;
  dropTarget: boolean;
  renaming: boolean;
  label: string;
  hint: string;
  nameDefault: string;
  selection: ArrangeSelection;
  tracks: PatternTrack[];
  renameRef: React.RefObject<HTMLInputElement | null>;
  onSelectPattern: () => void;
  onRenameStart: () => void;
  onRenameCommit: (raw: string) => void;
  onRenameCancel: () => void;
  onPatternContextMenu: (event: ReactMouseEvent) => void;
  onOpenTrack: (track: number) => void;
  onTrackContextMenu: (track: number, event: ReactMouseEvent) => void;
  onDragStart: (event: ReactDragEvent) => void;
  onDragOver: (event: ReactDragEvent) => void;
  onDrop: (event: ReactDragEvent) => void;
  onDragEnd: () => void;
};

function PatternMatrixRow({
  patternId,
  trackCount,
  rowBgSolid,
  isActive,
  isSelectedPattern,
  dropTarget,
  renaming,
  label,
  hint,
  nameDefault,
  selection,
  tracks,
  renameRef,
  onSelectPattern,
  onRenameStart,
  onRenameCommit,
  onRenameCancel,
  onPatternContextMenu,
  onOpenTrack,
  onTrackContextMenu,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: PatternMatrixRowProps) {
  const patternSelectedTone = isActive || isSelectedPattern
    ? "text-sky-100"
    : "text-slate-100";

  return (
    <>
      {/* Sticky pattern label — opaque bg so matrix cannot show through */}
      <div
        className={[
          "sticky z-10 flex min-w-0 cursor-default flex-col justify-center border-r border-b border-lab-line px-1.5",
          dropTarget ? "outline outline-1 outline-lab-accent -outline-offset-1" : "",
        ].join(" ")}
        data-arrange-pattern={patternId}
        data-arrange-bg={rowBgSolid}
        style={{ height: ROW_PX, left: LOOP_COL_PX, backgroundColor: rowBgSolid }}
        draggable={!renaming}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        onClick={onSelectPattern}
        onDoubleClick={onRenameStart}
        onContextMenu={onPatternContextMenu}
        role="rowheader"
      >
        {renaming ? (
          <input
            ref={renameRef}
            className={`${dockFieldControl} relative z-[1] !py-0.5 !text-[11px]`}
            defaultValue={nameDefault}
            placeholder={patternId}
            onClick={event => event.stopPropagation()}
            onBlur={event => onRenameCommit(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") {
                event.currentTarget.blur();
              } else if (event.key === "Escape") {
                onRenameCancel();
              }
            }}
          />
        ) : (
          <span className="relative z-[1] flex min-w-0 flex-col">
            <span className={`truncate text-[11px] font-medium leading-tight ${patternSelectedTone}`}>
              {label}
            </span>
            <span className="truncate font-mono text-[9px] leading-tight text-lab-muted">
              {hint}
            </span>
          </span>
        )}
      </div>

      {Array.from({ length: trackCount }, (_, track) => {
        const cells = tracks[track]?.cells;
        const hasNotes = trackHasContent(cells);
        const isMuted = tracks[track]?.muted === true;
        const trackSelected =
          selection.kind === "track"
          && selection.patternId === patternId
          && selection.track === track;

        return (
          <button
            key={`${patternId}-${track}`}
            type="button"
            role="gridcell"
            data-arrange-cell={`${patternId}:${track}`}
            data-arrange-pattern={patternId}
            data-arrange-bg={rowBgSolid}
            title={`${patternId} · T${track + 1}${isMuted ? " (muted)" : ""}`}
            className={[
              "relative box-border border-r border-b border-lab-line p-0 transition-colors rounded-none",
              trackSelected
                ? "z-[1] outline outline-1 outline-lab-accent -outline-offset-1"
                : "hover:brightness-110",
              dropTarget ? "outline outline-1 outline-lab-accent/40 -outline-offset-1" : "",
            ].join(" ")}
            style={{ height: ROW_PX, width: CELL_PX, backgroundColor: rowBgSolid }}
            onMouseDown={event => {
              // Don't steal focus from the arrange panel (needed for ⌘/Ctrl+C/V).
              event.preventDefault();
            }}
            onClick={event => {
              event.stopPropagation();
              onOpenTrack(track);
            }}
            onContextMenu={event => onTrackContextMenu(track, event)}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            <span
              className={[
                "absolute inset-[3px] z-[1] grid place-items-center rounded-[2px] border",
                hasNotes
                  ? "border-white/20 bg-[var(--arrange-cell-filled)]"
                  : "border-white/[0.06] bg-[var(--arrange-cell)]",
                isMuted ? "opacity-45" : "",
              ].join(" ")}
            >
              {isMuted ? (
                <span className="text-[10px] leading-none text-lab-muted" aria-hidden={true}>
                  ×
                </span>
              ) : hasNotes ? (
                <span
                  className="block h-px w-[60%] bg-white/35"
                  aria-hidden={true}
                />
              ) : null}
            </span>
            <ArrangeMeterOverlay patternId={patternId} track={track} />
          </button>
        );
      })}
    </>
  );
}

function ArrangeMeterOverlay({
  patternId,
  track,
}: {
  patternId: string;
  track: number;
}) {
  const leftRef = useRef<HTMLSpanElement>(null);
  const rightRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right) {
      return;
    }
    return registerArrangeMeterBars(patternId, track, left, right);
  }, [patternId, track]);

  return (
    <span
      className="pointer-events-none absolute inset-[3px] z-[2] overflow-hidden rounded-[2px]"
      aria-hidden={true}
    >
      <span
        ref={leftRef}
        className="absolute bottom-0 left-0 h-full w-[46%] origin-bottom bg-[#7dd3fc]/40 will-change-transform"
        style={{ transform: "scaleY(0)" }}
      />
      <span
        ref={rightRef}
        className="absolute bottom-0 right-0 h-full w-[46%] origin-bottom bg-[#7dd3fc]/40 will-change-transform"
        style={{ transform: "scaleY(0)" }}
      />
    </span>
  );
}
